import { SecondaryButton } from '../_shared/Buttons/SecondaryButton'
import { TextInput } from '../_shared/TextInput'
import { useEffect, useState } from 'react'
import { CustomSelect } from '../_shared/CustomSelect'
import { useChosenCompany, useUser } from '../../../../../context/UserProvider'
import { useMutation, useQueryClient } from 'react-query'
import { sendAdditionalProductEmail } from '../../../../../api/flow'
import { Loading } from '../_shared/Loading'
import { fuelList } from '../helpers/productKeys'
import Textarea from '../_shared/TextArea'
import { usePostalPlace } from '../../../../home/hooks/usePostalPlace'

type Payload = {
  regNr: string
  productType: string
  company: string
  address?: string
}

export function LostFueldCard({ id, car, setShowLostFuelCard }) {
  const products = car.additionalProducts
  const user = useUser()
  const userAddress = user?.user?.contactDetails?.address || ''
  const userName = user?.user?.contactDetails?.name || ''
  const postCode = user?.user?.contactDetails?.postCode || ''
  const company = useChosenCompany()
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const queryClient = useQueryClient()

  const findAllFuelCardOrChargingChipProducts = products
    .map((product) => {
      if (fuelList.includes(product.AgreementCode)) {
        return {
          value:
            product.ReferenceNo !== ''
              ? product.ReferenceNo
              : product.AgreementCode,
          label: product.Description,
        }
      }
      return null
    })
    .filter((product) => product !== null)

  const [payload, setPayload] = useState({
    regNumber: car.registrationNumber,
    cardChipType: findAllFuelCardOrChargingChipProducts[0]?.value,
    company: company?.name || '',
    address: user?.user?.contactDetails?.address || '',
    customerName: user?.user?.contactDetails?.name || '',
    postalCode: user?.user?.contactDetails?.postCode || '',
    postCity: '',
    additionalComment: '',
  })

  const postalPlace = usePostalPlace(payload.postalCode)

  useEffect(() => {
    if (postalPlace?.data?.result) {
      setPayload((prev) => {
        if (prev.postCity === postalPlace.data.result) return prev
        return {
          ...prev,
          postCity: postalPlace.data.result,
        }
      })
    }
  }, [postalPlace?.data?.result])

  const data = useMutation(
    (payload: Payload) => {
      return sendAdditionalProductEmail(payload)
    },
    {
      onSuccess: () => {
        setIsLoading(false)
        setSuccess(true)
        queryClient.refetchQueries('car')
      },
      onError: () => {
        setIsLoading(false)
      },
    },
  )

  function submit() {
    const formattedPayload = {
      regNr: payload.regNumber,
      productType: payload.cardChipType,
      company: payload.company,
      address: payload.address ?? '',
      customerName: payload.customerName,
      postalCode: payload.postalCode,
      postCity: payload.postCity,
      additionalComment: payload.additionalComment ?? '',
    }

    data.mutate(formattedPayload)
  }

  if (products.length === 0) {
    return (
      <div className="flex w-full items-center justify-center min-h-[200px] flex-col gap-6">
        <p className="text-center max-w-[40ch] leading-7">
          Du har ingen kort eller brikker som kan bestilles.
        </p>
        <SecondaryButton onClick={() => setShowLostFuelCard(false)}>
          Lukk vinduet
        </SecondaryButton>
      </div>
    )
  }

  if (data.isError) {
    return (
      <div className="flex w-full items-center justify-center min-h-[200px] flex-col gap-6">
        <p className="text-center max-w-[40ch] leading-7">
          Noe gikk galt under bestilling av kort/chip. Prøv igjen senere eller
          kontakt Autoplan.
        </p>
        <SecondaryButton onClick={() => setShowLostFuelCard(false)}>
          Lukk vinduet
        </SecondaryButton>
      </div>
    )
  }
  if (isLoading) {
    return (
      <Loading>
        <p>Sender forespørsel...</p>
      </Loading>
    )
  }

  if (success) {
    return (
      <div className="flex w-full items-center justify-center min-h-[200px] flex-col gap-6">
        <p className="text-xl">Nytt kort/brikke er bestilt!</p>
        <SecondaryButton onClick={() => setShowLostFuelCard(false)}>
          Lukk vinduet
        </SecondaryButton>
      </div>
    )
  }

  function setComment(val: string) {
    setPayload((prev) => ({
      ...prev,
      additionalComment: val,
    }))
  }
  return (
    <div className="flex flex-col gap-8 items-start">
      <div className="flex flex-col gap-4">
        <h3 className=" text-xl font-semibold">
          Mistet drivstoffkort eller ladebrikke?
        </h3>
        <p>Kortet/brikken vil bli sendt til din registrerte adresse.</p>
      </div>
      <div className="w-full flex flex-col gap-4">
        <TextInput
          id="regNumber"
          label="Registreringsnummer"
          value={payload.regNumber}
          onChange={(val) => setPayload({ ...payload, regNumber: val })}
        />
        <CustomSelect
          label="Type kort/brikke"
          value={payload.cardChipType}
          onChange={(val) => setPayload({ ...payload, cardChipType: val })}
          id="cardChipType"
          options={findAllFuelCardOrChargingChipProducts}
        />
        <div className="flex flex-col gap-1">
          <TextInput
            id="customerName"
            label="Navn"
            value={payload.customerName}
            onChange={(val) => setPayload({ ...payload, customerName: val })}
          />
        </div>
        <div className="flex flex-col gap-1">
          <TextInput
            id="address"
            label="Adresse"
            value={payload.address}
            onChange={(val) => setPayload({ ...payload, address: val })}
          />
        </div>
        <div className="flex flex-row items-center justify-between [&>*]:w-full gap-2">
          <div className="flex flex-col gap-1">
            <TextInput
              id="postalCode"
              label="Postnummer"
              value={payload.postalCode}
              onChange={(val) => setPayload({ ...payload, postalCode: val })}
            />
          </div>
          <div className="flex flex-col gap-1">
            <TextInput
              id="postCity"
              disabled
              label="Poststed"
              value={payload.postCity}
              onChange={(val) => setPayload({ ...payload, postCity: val })}
            />
          </div>
        </div>
        <Textarea
          value={payload.additionalComment}
          onChange={setComment}
          id={'additionalComment'}
          label={'Kommentar'}
          placeholder={'Skriv en kommentar'}
        />
      </div>
      <SecondaryButton onClick={() => submit()}>Bestill</SecondaryButton>
    </div>
  )
}
